import React, { ChangeEvent, useEffect } from 'react';
import { useHistory, Redirect } from 'react-router-dom';
import Axios from 'axios';

import { Button, Loader, Input, WebCopy, WebHeading } from 'components/shared';
import Icon from 'assets/icons';

import { gql } from 'apollo-boost';
import { useQuery } from '@apollo/react-hooks';
import { useTranslation } from 'hooks';
import { AsyncStorage } from 'services';
import { OrganizationTypes } from 'helpers';
import { isNewOrganization } from 'helpers/organizations';
import { organizationDomainSuffix } from 'helpers/constants';
import { apiOrganizationEndpoints } from 'helpers/endpoints';
import { trackingOnboardingOrganizationSetupView } from 'services/analytics';

import styles from './SetupOrganization.module.scss';
import obstyles from '../../OnboardingScreen.module.scss';

const SLUG_VALIDATOR = /^\w[\w-]*$/;

interface SetupOrganizationData {
  me: {
    organization: Organization;
  };
}

const getOrganizationQuery = gql`
  query getOrganization {
    me {
      organization {
        id
        name
        slug
        type
        memberCount
      }
    }
  }
`;

export const SetupOrganization = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const [name, setName] = React.useState<string>('');
  const [inputError, setInputError] = React.useState('');

  const [saving, setSaving] = React.useState(false);
  const [editing, setEditing] = React.useState(false);

  const { error, data } = useQuery<SetupOrganizationData>(getOrganizationQuery, {
    fetchPolicy: 'cache-and-network',
  });

  const isNewOrg = isNewOrganization(data?.me.organization);
  const redirectUser = data?.me.organization.type !== OrganizationTypes.WORK || !isNewOrg;

  useEffect(() => {
    if (!redirectUser) {
      trackingOnboardingOrganizationSetupView();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (data === undefined) return <Loader />;

  if (error) return <Loader color="Red" />;

  if (redirectUser) {
    return <Redirect to="/onboarding/add-team" />;
  }

  /**
   * Called when the input field is focused
   */
  const handleInputFocus = () => {
    if (name === data.me.organization.slug) {
      setName('');
    }
    setEditing(true);
  };

  /**
   * Called when the input field is blurred
   */
  const handleInputBlur = () => {
    if (name === '' && data.me.organization.slug) {
      setName(data.me.organization.slug);
    }
  };

  /**
   * Called when the continue button is clicked
   * If there's no input errors it will set the organization name
   * and redirect to the next onboarding page
   */
  const handleContinue = async () => {
    /**
     * If the user made no change to the organization default slug
     * we simply advance to the next onboarding step
     */
    if (!editing) {
      history.push('/onboarding/add-team');
      return;
    }

    const trimmed = name ? name.trim().toLocaleLowerCase() : '';
    const endpoint = apiOrganizationEndpoints(data.me.organization.id!).updateOrganization;

    // Name cannnot be empty
    if (!name) {
      setInputError(t('onboarding.setup-organization.errors.empty-name'));
      return;
    }

    // Name cannnot be shorter than 4 characters
    if (trimmed.length < 4) {
      setInputError(t('onboarding.setup-organization.errors.name-too-short'));
      return;
    } else if (!SLUG_VALIDATOR.test(trimmed)) {
      setInputError(t('onboarding.setup-organization.errors.invalid-slug'));
      return;
    }

    setSaving(true);

    const refreshToken = await AsyncStorage.get('refreshToken');

    Axios.put(
      endpoint,
      { name: trimmed, slug: trimmed },
      {
        headers: {
          'Content-Type': 'application/json',
          'fyi-organization': data.me.organization.id,
          'fyi-authorization': 'Bearer ' + refreshToken,
        },
      }
    )
      .then(() => {
        setSaving(false);
        history.push('/onboarding/add-team');
      })
      .catch(() => {
        setSaving(false);
        setInputError(t('onboarding.setup-organization.errors.save-error'));
      });
  };

  return (
    <div className={obstyles.contentLayout}>
      <div className={styles.contentWrapper}>
        <WebHeading className={styles.title} level={1}>
          {t('onboarding.setup-organization.heading')}
        </WebHeading>

        <WebCopy className={styles.copy}>{t('onboarding.setup-organization.text')}</WebCopy>
      </div>

      <div className={styles.formWrapper}>
        <div className={styles.formRow}>
          <WebCopy className={styles.formLabel} type="Label">
            {t('onboarding.setup-organization.org-name')}
          </WebCopy>

          <Input
            fullWidth
            error={!!inputError}
            inputClassName={styles.textInput}
            placeholder="Name"
            suffix={<span className={styles.domain}>{organizationDomainSuffix}</span>}
            onFocus={handleInputFocus}
            onBlur={handleInputBlur}
            theme="WebLight"
            value={editing ? name : data.me.organization.slug}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setName(e.target.value)}
          />

          {inputError && (
            <WebCopy className={styles.error} type="Error">
              {inputError}
            </WebCopy>
          )}
        </div>
      </div>

      <div className={obstyles.footer}>
        <Button disabled={saving} size="Large" onClick={handleContinue}>
          {t('onboarding.continue')}
          {!saving && <Icon.ChevronThick textPosition="Right" />}
          {saving && <Loader className={styles.loader} color="White" width={30} height={30} />}
        </Button>
      </div>
    </div>
  );
};

export default SetupOrganization;
